import { getStartOf, parseDateFromISO8601, stringifyDateToISO8601 } from './dates';
import { isInstantBooking } from './genericHelpers';

export const AVAILABILITY_LOOKAHEAD_DAYS = 90;

const DEFAULT_TIME_ZONE = 'Etc/UTC';
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const normalizeDateId = (value, timeZone) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!ISO_DATE_PATTERN.test(trimmed)) {
    return null;
  }

  const parsed = parseDateFromISO8601(trimmed, timeZone);
  return Number.isNaN(parsed.getTime()) ? null : stringifyDateToISO8601(parsed, timeZone);
};

const hasAvailabilityOnDate = (timeSlotsByDate, dateId) => {
  return timeSlotsByDate?.[dateId]?.hasAvailability === true;
};

const getStoredAvailableBookingDate = ({ listing, availabilityData }) => {
  return (
    availabilityData?.availableBookingDate ||
    listing?.attributes?.publicData?.availableBookingDate ||
    null
  );
};

const findFirstBookableDateFromTimeSlots = params => {
  const { timeSlotsByDate, todayStart, timeZone, allowsSameDayBooking, lookaheadDays } = params;

  for (let offset = 0; offset <= lookaheadDays; offset += 1) {
    if (offset === 0 && !allowsSameDayBooking) {
      continue;
    }

    const day = getStartOf(todayStart, 'day', timeZone, offset, 'days');
    const dateId = stringifyDateToISO8601(day, timeZone);
    if (hasAvailabilityOnDate(timeSlotsByDate, dateId)) {
      return dateId;
    }
  }

  return null;
};

const selectFirstBookableDateId = params => {
  const { storedDateId, computedDateId, todayId, isBookableToday, allowsSameDayBooking } = params;

  if (!storedDateId) {
    return computedDateId;
  }

  const storedIsToday = storedDateId === todayId;
  if (storedIsToday && (!allowsSameDayBooking || !isBookableToday)) {
    return computedDateId;
  }

  if (!computedDateId) {
    return storedDateId;
  }

  // If stored value points to an earlier date than computed value, it is stale.
  return storedDateId < computedDateId ? computedDateId : storedDateId;
};

const getBookableDataFromTimeSlots = params => {
  const {
    listing,
    availabilityData,
    timeSlotsByDate,
    timeZone,
    now,
    lookaheadDays = AVAILABILITY_LOOKAHEAD_DAYS,
  } = params;

  const todayStart = getStartOf(now, 'day', timeZone);
  const todayId = stringifyDateToISO8601(todayStart, timeZone);
  const allowsSameDayBooking = isInstantBooking(listing);
  const isBookableToday = allowsSameDayBooking && hasAvailabilityOnDate(timeSlotsByDate, todayId);

  const computedDateId = isBookableToday
    ? todayId
    : findFirstBookableDateFromTimeSlots({
        timeSlotsByDate,
        todayStart,
        timeZone,
        allowsSameDayBooking,
        lookaheadDays,
      });
  const storedDateId = normalizeDateId(
    getStoredAvailableBookingDate({ listing, availabilityData }),
    timeZone
  );

  const firstBookableDateId = isBookableToday
    ? todayId
    : selectFirstBookableDateId({
        storedDateId,
        computedDateId,
        todayId,
        isBookableToday,
        allowsSameDayBooking,
      });

  return {
    isBookableToday,
    firstBookableDateId,
  };
};

const getBookableDataFromAvailabilityData = params => {
  const { listing, availabilityData, timeZone, now } = params;
  const todayStart = getStartOf(now, 'day', timeZone);
  const todayId = stringifyDateToISO8601(todayStart, timeZone);
  const allowsSameDayBooking = isInstantBooking(listing);

  const dataSaysBookableToday =
    availabilityData?.isBookableToday === true || availabilityData?.availableTonight === true;
  const isBookableToday = allowsSameDayBooking && dataSaysBookableToday;

  const firstDateFromData = normalizeDateId(
    availabilityData?.firstBookableDate || availabilityData?.firstAvailableDate,
    timeZone
  );
  const storedDateId = normalizeDateId(
    getStoredAvailableBookingDate({ listing, availabilityData }),
    timeZone
  );
  const preferredDateId = firstDateFromData || storedDateId;
  const firstBookableDateId =
    isBookableToday || preferredDateId !== todayId ? preferredDateId : null;

  return {
    isBookableToday,
    firstBookableDateId: isBookableToday ? todayId : firstBookableDateId,
  };
};

const formatFirstBookableDate = ({ intl, date, dateId, timeZone }) => {
  if (!date) {
    return null;
  }

  if (!intl?.formatDate) {
    return dateId;
  }

  return intl.formatDate(date, {
    month: 'short',
    day: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  });
};

export const resolveAvailabilityTimeZone = ({
  listing,
  availabilityData,
  config,
  fallbackTimeZone = DEFAULT_TIME_ZONE,
} = {}) => {
  return (
    availabilityData?.timeZone ||
    listing?.attributes?.availabilityPlan?.timezone ||
    config?.localization?.timeZone ||
    fallbackTimeZone
  );
};

export const getAvailabilityBadgeData = params => {
  const {
    listing,
    availabilityData = {},
    timeSlotsByDate,
    config,
    intl,
    now = new Date(),
    lookaheadDays = AVAILABILITY_LOOKAHEAD_DAYS,
    timeZone,
  } = params || {};

  const resolvedTimeZone =
    timeZone ||
    resolveAvailabilityTimeZone({
      listing,
      availabilityData,
      config,
    });

  const { isBookableToday, firstBookableDateId } = timeSlotsByDate
    ? getBookableDataFromTimeSlots({
        listing,
        availabilityData,
        timeSlotsByDate,
        timeZone: resolvedTimeZone,
        now,
        lookaheadDays,
      })
    : getBookableDataFromAvailabilityData({
        listing,
        availabilityData,
        timeZone: resolvedTimeZone,
        now,
      });

  const firstBookableDate = firstBookableDateId
    ? parseDateFromISO8601(firstBookableDateId, resolvedTimeZone)
    : null;
  const formattedDate = formatFirstBookableDate({
    intl,
    date: firstBookableDate,
    dateId: firstBookableDateId,
    timeZone: resolvedTimeZone,
  });

  const label = isBookableToday
    ? 'Available tonight'
    : formattedDate
    ? `Available starting ${formattedDate}`
    : 'Check availability';

  return {
    label,
    isBookableToday,
    firstBookableDate,
    firstBookableDateId,
    timeZone: resolvedTimeZone,
  };
};
